/**
 * AccountSetup Schritte: Passwort + PIN — als reine Sub-Komponenten extrahiert.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Lock, KeyRound, ChevronRight, CheckCircle2, Loader2 } from 'lucide-react';

export const Requirement = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-muted-foreground'}`}>
    <CheckCircle2 className={`h-3.5 w-3.5 ${met ? 'text-green-500' : 'text-muted-foreground/40'}`} />
    {text}
  </div>
);

export const PasswordStep = ({
  newPassword, setNewPassword, confirmPassword, setConfirmPassword,
  rules, passwordValid, onNext,
}) => (
  <Card className="border-border/60 animate-fade-in" data-testid="step-password">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <Lock className="h-5 w-5 text-primary" /> Neues Passwort setzen
      </CardTitle>
      <CardDescription>Legen Sie ein sicheres Passwort fuer Ihr Konto fest</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="new_password">Neues Passwort</Label>
        <Input id="new_password" type="password" value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)} placeholder="Mindestens 8 Zeichen"
          data-testid="input-new-password" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirm_password">Passwort bestaetigen</Label>
        <Input id="confirm_password" type="password" value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Passwort wiederholen"
          data-testid="input-confirm-password" />
      </div>
      <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
        <Requirement met={rules.hasMinLength} text="Mindestens 8 Zeichen" />
        <Requirement met={rules.hasUppercase} text="Mindestens ein Grossbuchstabe" />
        <Requirement met={rules.hasLowercase} text="Mindestens ein Kleinbuchstabe" />
        <Requirement met={rules.hasNumber} text="Mindestens eine Zahl" />
        <Requirement met={rules.hasSpecial} text="Mindestens ein Sonderzeichen" />
        <Requirement met={rules.passwordsMatch} text="Passwoerter stimmen ueberein" />
      </div>
      <Button className="w-full rounded-full" onClick={onNext} disabled={!passwordValid}
        data-testid="btn-next-step">
        Weiter <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </CardContent>
  </Card>
);

const sanitizePin = (raw, current) => {
  const v = raw.replace(/\D/g, '');
  return v.length <= 4 ? v : current;
};

export const PinStep = ({
  pin, setPin, confirmPin, setConfirmPin,
  pinValid, pinsMatch, submitting, onBack, onSubmit,
}) => (
  <Card className="border-border/60 animate-fade-in" data-testid="step-pin">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <KeyRound className="h-5 w-5 text-primary" /> Zeiterfassungs-PIN festlegen
      </CardTitle>
      <CardDescription>
        Dieser 4-stellige PIN wird fuer die Stempeluhr am Terminal verwendet
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pin">4-stelliger PIN</Label>
        <Input id="pin" type="password" inputMode="numeric" maxLength={4} value={pin}
          onChange={(e) => setPin(sanitizePin(e.target.value, pin))}
          placeholder="4 Ziffern"
          className="text-center text-2xl tracking-[0.5em] font-mono"
          data-testid="input-pin" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirm_pin">PIN bestaetigen</Label>
        <Input id="confirm_pin" type="password" inputMode="numeric" maxLength={4} value={confirmPin}
          onChange={(e) => setConfirmPin(sanitizePin(e.target.value, confirmPin))}
          placeholder="PIN wiederholen"
          className="text-center text-2xl tracking-[0.5em] font-mono"
          data-testid="input-confirm-pin" />
      </div>
      <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
        <Requirement met={pinValid} text="Genau 4 Ziffern" />
        <Requirement met={pinsMatch} text="PINs stimmen ueberein" />
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="rounded-full" onClick={onBack} data-testid="btn-back">
          Zurueck
        </Button>
        <Button className="flex-1 rounded-full" onClick={onSubmit}
          disabled={!pinValid || !pinsMatch || submitting} data-testid="btn-complete-setup">
          {submitting
            ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
            : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Konto einrichten
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const StepIndicator = ({ step }) => (
  <div className="flex items-center justify-center gap-3" data-testid="step-indicator">
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
      step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
    }`}>
      <Lock className="h-3 w-3" /> Passwort
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
      step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
    }`}>
      <KeyRound className="h-3 w-3" /> PIN
    </div>
  </div>
);
